// Aba aberta antes de um deploy: o index em memória aponta pros chunks da
// versão anterior, que o deploy apagou, e a próxima tela lazy falha com
// "Failed to fetch dynamically imported module". Recarregar pega o index novo.
// Uma vez só por janela de 10 s: se o chunk falhar de novo (rede caída), a
// tela de erro aparece em vez de a página recarregar sem parar.
//
// Só recarregar não basta: o Hosting respondia a um asset que não existe com o
// index.html, status 200 e o `immutable` de um ano da regra de /app/assets. O
// navegador guarda esse HTML no endereço do chunk, e o reload (e o F5) pedem o
// mesmo endereço e falham de novo, mesmo depois de o arquivo existir. Por isso,
// antes de recarregar, o arquivo é pedido outra vez com `cache: 'reload'`, que
// vai à rede e troca a entrada do cache.
//
// E não só ele: o Chrome põe na mensagem o módulo que a tela importou, mesmo
// quando quem falhou foi uma dependência dele (2026-10-02: "Anuncios-….js"
// no erro, com o Anuncios e as 77 dependências íntegras no servidor). Então
// as dependências estáticas também são pedidas de novo, seguindo os `import`
// de cada resposta. Os `import("…")` dinâmicos ficam de fora: são as outras
// telas, que se consertam quando forem abertas.

import { lazy, type ComponentType } from 'react';
import { voltarCortina } from './ui/boot';

const CHAVE = 'recarregou-por-chunk';
const JANELA_MS = 10_000;
/** Teto de arquivos por conserto: o grafo de uma tela tem uns 15. */
const LIMITE = 60;
const IMPORT_ESTATICO = /(?:\bfrom|\bimport)\s*["'`](\.{1,2}\/[^"'`]+?\.(?:js|css))["'`]/g;

/** A URL que o Vite põe na mensagem ("…module: https://…" ou "…CSS for https://…"). */
export function urlDoErro(mensagem: string | undefined): string | undefined {
  return /https?:\/\/\S+/.exec(mensagem ?? '')?.[0];
}

/**
 * Pede de novo, da rede, o arquivo e as dependências estáticas dele. Nível a
 * nível, cada nível em paralelo: a cortina fica na tela enquanto isto roda.
 */
export async function consertarCache(url: string, buscar: typeof fetch = fetch): Promise<string[]> {
  const vistos = new Set<string>();
  let nivel = [url];
  while (nivel.length && vistos.size < LIMITE) {
    const novos = [...new Set(nivel)].filter((u) => !vistos.has(u)).slice(0, LIMITE - vistos.size);
    novos.forEach((u) => vistos.add(u));
    const filhos = await Promise.all(novos.map(async (atual) => {
      let resposta: Response;
      try { resposta = await buscar(atual, { cache: 'reload' }); } catch { return []; }
      if (!resposta.ok || !/javascript/.test(resposta.headers.get('content-type') ?? '')) return [];
      const texto = await resposta.text();
      return [...texto.matchAll(IMPORT_ESTATICO)].map((m) => new URL(m[1], atual).href);
    }));
    nivel = filhos.flat();
  }
  return [...vistos];
}

type Janela = Pick<Window, 'addEventListener' | 'sessionStorage' | 'location'>;

let ambiente: { janela: Janela; buscar: typeof fetch } | undefined;
let recarregando = false;

/**
 * Decide, uma vez por falha, se a página vai recarregar: `true` quando o
 * reload ficou agendado (agora ou por quem chegou antes), `false` quando a
 * trava dos 10 s segura — aí a falha é de verdade e a tela de erro aparece.
 * O ouvinte do Vite e o `lazyDaTela` passam os dois por aqui: o evento chega
 * primeiro, e a promessa rejeitada da tela encontra o reload já agendado.
 */
export function recuperar(mensagem: string | undefined): boolean {
  if (recarregando) return true;
  const { janela, buscar } = ambiente ?? { janela: window, buscar: fetch };
  try {
    if (Date.now() - Number(janela.sessionStorage.getItem(CHAVE) || 0) < JANELA_MS) return false;
    janela.sessionStorage.setItem(CHAVE, String(Date.now()));
  } catch { return false; /* sem sessionStorage não há trava: melhor a tela de erro que um laço */ }
  recarregando = true;
  voltarCortina();
  const url = urlDoErro(mensagem);
  const conserto = url ? consertarCache(url, buscar).catch(() => undefined) : Promise.resolve();
  void conserto.finally(() => janela.location.reload());
  return true;
}

export function armarRecargaDeChunk(janela: Janela = window, buscar: typeof fetch = fetch) {
  ambiente = { janela, buscar };
  janela.addEventListener('vite:preloadError', (evento) => { recuperar(evento.payload?.message); });
}

/**
 * O `lazy` das telas. Com o `lazy` puro, a falha do chunk subia até o React
 * enquanto o reload não acontecia: a tela de erro piscava e, logo depois, a
 * página recarregava (2026-10-08). Aqui, se o reload ficou agendado, a
 * promessa fica pendente para sempre — o Suspense segue no "Abrindo…" até a
 * página trocar. Só a falha que a trava não deixa recarregar chega ao React.
 */
export function lazyDaTela<T extends ComponentType<any>>(carregar: () => Promise<{ default: T }>) {
  return lazy(() => carregar().catch((erro: unknown) => {
    if (recuperar(erro instanceof Error ? erro.message : String(erro))) return new Promise<never>(() => {});
    throw erro;
  }));
}
